import React, { useState, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { useAlbums } from '../context/AlbumsContext';
import Navbar from '../components/Navbar';
import ProgressBar from '../components/ProgressBar';
import SearchFilter from '../components/SearchFilter';
import { GRUPOS_ORDENADOS, GRUPOS_ESPECIAIS, ORDEM_TIMES_POR_GRUPO } from '../data/copa2026';
import Logo from '../images/caca_figurinhas_logo_v2.svg';
import './Faltam.css';

const TOTAL_FIGURINHAS = 998;
const ANIMATION_DURATION = 500;

const Faltam = ({ onNavigate }) => {
  const { albums, activeAlbumId, isLoading, stickers, toggleFigurinha, setAlbumActive } = useAlbums();
  const [filtro, setFiltro] = useState('');
  const [removingCards, setRemovingCards] = useState({});

  const totalObtidas = stickers
    ? Object.values(stickers).reduce((acc, pais) =>
        acc + Object.values(pais.jogadores).filter((j) => j.obtida).length, 0)
    : 0;

  const todosTimes = useMemo(() => {
    if (!stickers) return [];
    return GRUPOS_ORDENADOS.flatMap((g) =>
      (ORDEM_TIMES_POR_GRUPO[g] || []).map((sigla) => stickers[sigla]).filter(Boolean)
    );
  }, [stickers]);

  const gruposEspeciais = useMemo(() => {
    if (!stickers) return [];
    return GRUPOS_ESPECIAIS.map((g) => stickers[g]).filter(Boolean);
  }, [stickers]);


  const handleTrocarAlbum = (e) => {
    const albumId = e.target.value;
    const album = albums.find((a) => a.id === albumId);

    ReactGA.event({
      category: 'Navigation',
      action: 'Album Changed',
      label: album?.name || 'Unknown',
    });

    setAlbumActive(albumId);
  };

  const handleJaConsegui = (sigla, key) => {
    const cardId = `${sigla}-${key}`;
    setRemovingCards((prev) => ({ ...prev, [cardId]: true }));
    setTimeout(() => {
      toggleFigurinha(activeAlbumId, sigla, key);
      setRemovingCards((prev) => {
        const next = { ...prev };
        delete next[cardId];
        return next;
      });
    }, ANIMATION_DURATION);
  };

  const getFigurinhasFaltantes = (pais) => {
    if (!pais || !pais.jogadores) return [];
    return Object.entries(pais.jogadores)
      .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
      .filter(([_, jogador]) => !jogador.obtida)
      .map(([key, jogador]) => ({
        key,
        ...jogador,
      }));
  };

  const figurinhasFaltantes = useMemo(() => {
    if (!stickers) return [];

    const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const fuzzyMatch = (haystack, needle) => {
      let ni = 0;
      for (let hi = 0; hi < haystack.length && ni < needle.length; hi++) {
        if (haystack[hi] === needle[ni]) ni++;
      }
      return ni === needle.length;
    };
    const normFiltro = norm(filtro).replace(/[^a-z0-9]/g, '');

    // Se houver filtro, filtrar jogadores por nome ou país
    if (normFiltro) {
      const grupos = [];

      // Buscar em grupos especiais
      const paisesEspeciais = gruposEspeciais
        .map((p) => ({
          ...p,
          figurinhas: getFigurinhasFaltantes(p).filter(fig =>
            fuzzyMatch(norm(fig.nome).replace(/[^a-z0-9]/g, ''), normFiltro) ||
            fuzzyMatch(norm(p.nome).replace(/[^a-z0-9]/g, ''), normFiltro)
          ),
        }))
        .filter((p) => p.figurinhas.length > 0);

      if (paisesEspeciais.length > 0) {
        grupos.push({ tipo: 'especial', paises: paisesEspeciais });
      }

      // Buscar em todos os países
      GRUPOS_ORDENADOS.forEach((grupo) => {
        const paisesDoGrupo = todosTimes
          .filter((p) => p.grupo === grupo)
          .map((p) => ({
            ...p,
            figurinhas: getFigurinhasFaltantes(p).filter(fig =>
              fuzzyMatch(norm(fig.nome).replace(/[^a-z0-9]/g, ''), normFiltro) ||
              fuzzyMatch(norm(p.nome).replace(/[^a-z0-9]/g, ''), normFiltro)
            ),
          }))
          .filter((p) => p.figurinhas.length > 0);

        if (paisesDoGrupo.length > 0) {
          grupos.push({ tipo: 'grupo', grupo, paises: paisesDoGrupo });
        }
      });

      return grupos;
    }

    // Sem filtro, mostrar todas as figurinhas faltantes
    const grupos = [];

    const paisesEspeciais = gruposEspeciais
      .map((p) => ({
        ...p,
        figurinhas: getFigurinhasFaltantes(p),
      }))
      .filter((p) => p.figurinhas.length > 0);

    if (paisesEspeciais.length > 0) {
      grupos.push({ tipo: 'especial', paises: paisesEspeciais });
    }

    GRUPOS_ORDENADOS.forEach((grupo) => {
      const paisesDoGrupo = todosTimes
        .filter((p) => p.grupo === grupo)
        .map((p) => ({
          ...p,
          figurinhas: getFigurinhasFaltantes(p),
        }))
        .filter((p) => p.figurinhas.length > 0);

      if (paisesDoGrupo.length > 0) {
        grupos.push({ tipo: 'grupo', grupo, paises: paisesDoGrupo });
      }
    });

    return grupos;
  }, [stickers, filtro, todosTimes, gruposEspeciais]);

  return (
    <div className="faltam">
      <header className="faltam-header">
        <div className="header-top">
          <img src={Logo} alt="Caça Figurinhas Logo" className="logo-icon" />
          <h1 className="app-title">Caça Figurinhas</h1>
        </div>

        <div className="header-controls">
          <label className="album-select-label" htmlFor="album-select">Álbum</label>
          <select
            id="album-select"
            className="album-select"
            value={activeAlbumId || ''}
            onChange={handleTrocarAlbum}
          >
            <option value="" disabled>Selecione um álbum</option>
            {albums.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        <div className="progresso-global">
          <ProgressBar obtidas={totalObtidas} total={TOTAL_FIGURINHAS} size="large" />
        </div>
      </header>

      <Navbar activeTab="faltam" onNavigate={onNavigate} activeAlbumId={activeAlbumId} isLoading={isLoading} />

      <main className="faltam-main">
        {!activeAlbumId ? (
          <div className="sem-album">
            <p>Nenhum álbum selecionado.</p>
            <button className="btn-voltar" onClick={() => onNavigate('albums')}>
              Ir para Meus Álbuns
            </button>
          </div>
        ) : (
          <>
            <div className="filtro-wrap">
              <SearchFilter
                onSearch={setFiltro}
                placeholder="Pesquisar país ou jogador..."
              />
            </div>

            {figurinhasFaltantes.length === 0 ? (
              <div className="nenhuma-faltando">
                <p>🎉 Parabéns! Você tem todas as figurinhas!</p>
              </div>
            ) : (
              <div className="figurinhas-grupos">
                {figurinhasFaltantes.map((section, idx) => (
                  <div key={section.tipo === 'especial' ? 'especial' : section.grupo || `s-${idx}`} className="figurinhas-section">
                    {section.tipo === 'especial' && (
                      <h2 className="figurinhas-titulo grupo-titulo-especial">Especiais</h2>
                    )}
                    {section.tipo === 'grupo' && (
                      <h2 className="figurinhas-titulo">Grupo {section.grupo}</h2>
                    )}

                    {section.paises.map((pais) => (
                      <div key={pais.sigla} className="pais-figurinhas">
                        <h3 className="pais-nome">{pais.flag} {pais.nome}</h3>
                        <div className="figurinhas-grid">
                          {pais.figurinhas.map((fig) => {
                            const cardId = `${pais.sigla}-${fig.key}`;
                            const isRemoving = removingCards[cardId];
                            return (
                              <div
                                key={fig.key}
                                className={`figurinha-card ${isRemoving ? 'removing' : ''}`}
                              >
                                <div className="figurinha-card-header">
                                  <div className="figurinha-card-header-top">
                                    <span className="figurinha-card-flag">{pais.flag}</span>
                                    <span className="figurinha-card-numero">#{fig.numero}</span>
                                  </div>
                                  <div className="figurinha-card-pais-nome" title={pais.nome}>
                                    {pais.nome}
                                  </div>
                                </div>
                                <div className="figurinha-card-body">
                                  <div className="figurinha-card-nome">{fig.nome}</div>
                                  <button
                                    className="figurinha-card-btn"
                                    onClick={() => handleJaConsegui(pais.sigla, fig.key)}
                                    disabled={isRemoving}
                                  >
                                    ✓ Já Consegui
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Faltam;
